/**
 * LooseRelationshipEditorDialog — shared Dialog wrapper around
 * LooseRelationshipTargetPicker (horizontal layout) used for editing and
 * "add another" flows. Replaces the cramped inline w-96 embed previously
 * rendered inside the FK popover.
 *
 * Header surfaces:
 *  - An (i) info tooltip explaining when a single column can host multiple
 *    loose FKs (polymorphic dispatch via source filters, or JSON sourcePath).
 *  - A "+ Add another" button that flips edit mode into add-another mode in
 *    place, keeping the source column locked.
 */

import { useEffect, useMemo, useState } from 'react';
import { Info, Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { DocsLinkButton } from '@/features/help-feedback';
import { LooseRelationshipTargetPicker } from './LooseRelationshipTargetPicker';
import { ProvenanceBadge } from '../meta-info/ProvenanceBadge';
import { useMetaInfo } from '../../hooks/useMetaInfo';
import { renderLooseFkLabel } from '../../utils/looseFkLabels';
import { useAnalytics } from '../../../../../core/analytics';
import type {
  InferSource,
  InferTable,
} from '../../utils/inferLooseRelationship';
import type { LooseRelationship } from '../../services/metaInfoApi';
import type { FilterGroup } from '../../../core/types/workspaceTypes';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import type { TableExplorerMetadataEditorProviders } from '../../../table-explorer/components/tableExplorerHostPorts';

type LooseRelationshipEditorDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionId: string;
  database: string;
  source: InferSource;
  allTables: InferTable[];
  engine?: DatabaseEngineId;

  /**
   * When set, the dialog opens in edit mode for the matching loose FK.
   * When undefined, the dialog opens in add-another mode.
   */
  existingRelationshipId?: string;
  initialTarget?: { schema: string; table: string; column: string };
  initialSourceFilter?: FilterGroup[];
  initialTargetFilter?: FilterGroup[];
  initialSourcePath?: string;

  /** Analytics source for the `feature:loose_fk_added` event (add path only). */
  addedSource?: string;
  /**
   * Save handler. When provided, the host controls what happens after a save
   * (e.g. exit edit-mode but stay in a parent picker). When omitted, the
   * dialog closes itself.
   */
  onSaved?: (rel: LooseRelationship) => void;
  /**
   * Cancel handler. When provided, host controls cancel; otherwise the dialog
   * closes itself.
   */
  onCancel?: () => void;
  metadataEditors?: TableExplorerMetadataEditorProviders;
  /**
   * Forwarded to the dialog content. A host that renders this dialog in
   * place of its own (FkLookupPicker) passes its focus-return handler so
   * closing from here lands focus on the host's opener, not `<body>`.
   */
  onCloseAutoFocus?: (event: Event) => void;
};

export function LooseRelationshipEditorDialog({
  open,
  onOpenChange,
  connectionId,
  database,
  source,
  allTables,
  engine,
  existingRelationshipId,
  initialTarget,
  initialSourceFilter,
  initialTargetFilter,
  initialSourcePath,
  addedSource = 'inline',
  onSaved,
  onCancel,
  metadataEditors,
  onCloseAutoFocus,
}: LooseRelationshipEditorDialogProps) {
  // When the host opens us with `existingRelationshipId`, we start in edit
  // mode. The user can flip into add mode via the header button — that's
  // tracked here so we can re-mount the picker with no edit context.
  const [inAddMode, setInAddMode] = useState(!existingRelationshipId);
  // When the source column has >1 declarations, the dialog renders a tab
  // strip so the user can switch between them without closing. `activeRelId`
  // is the currently-selected tab — starts at the host-passed id and flips
  // when the user clicks another tab.
  const [activeRelId, setActiveRelId] = useState<string | undefined>(
    existingRelationshipId,
  );
  const { track } = useAnalytics();

  // Reset add-mode + active tab when the dialog opens against a new context
  // — keeps the next "Edit" click from landing inside a stale form / tab.
  useEffect(() => {
    if (open) {
      setInAddMode(!existingRelationshipId);
      setActiveRelId(existingRelationshipId);
    }
  }, [open, existingRelationshipId]);

  // All declarations on the source `(schema, table, column)` triple — drives
  // the tab strip. The lookup runs against the live MetaInfo so newly added
  // declarations (via "Add another" or another surface) show up immediately
  // after MetaInfo invalidates.
  const { data: metaInfo } = useMetaInfo(connectionId, database);
  const allRels = useMemo(
    () =>
      (metaInfo?.looseRelationships ?? []).filter(
        (r) =>
          r.fromSchema === source.schema &&
          r.fromTable === source.table &&
          r.fromColumn === source.name,
      ),
    [metaInfo, source.schema, source.table, source.name],
  );

  // Active rel resolved from MetaInfo — preferred over the host-provided
  // initial* props because the user may have switched tabs since open.
  const activeRel = useMemo(
    () => (activeRelId ? allRels.find((r) => r.id === activeRelId) : undefined),
    [allRels, activeRelId],
  );

  // Host-provided initial* match the host's `existingRelationshipId`. We use
  // them as a fallback only when MetaInfo hasn't loaded yet (otherwise prefer
  // the live data, since tab switches and concurrent edits change it).
  const useHostFallback =
    activeRelId === existingRelationshipId && !activeRel;

  const isEditing = !inAddMode && !!activeRelId;
  // When the user switched from edit → add inside the dialog, tag the save
  // event with a distinct entry-point string so we can measure how often
  // this in-dialog affordance is used vs the popover-footer entry.
  const switchedFromEdit = inAddMode && !!existingRelationshipId;
  const effectiveAddedSource = switchedFromEdit
    ? 'dialog_add_another_button'
    : addedSource;

  // C2 N25: "another" only when the column already has one — a first declaration is not "another" (every engine).
  const title = isEditing
    ? 'Edit loose relationship'
    : allRels.length > 0
      ? `Declare another loose relationship for ${source.name}`
      : `Declare a loose relationship for ${source.name}`;

  // Picker initial-state inputs for the active tab. When the active rel
  // resolves from MetaInfo, use its values; otherwise fall back to host
  // props on first paint (before MetaInfo loads).
  const effectiveExistingId = isEditing ? activeRelId : undefined;
  const effectiveTarget = isEditing
    ? activeRel
      ? {
          schema: activeRel.toSchema,
          table: activeRel.toTable,
          column: activeRel.toColumn,
        }
      : useHostFallback
        ? initialTarget
        : undefined
    : undefined;
  const effectiveSourceFilter = isEditing
    ? (activeRel?.sourceFilter ?? (useHostFallback ? initialSourceFilter : undefined))
    : undefined;
  const effectiveTargetFilter = isEditing
    ? (activeRel?.targetFilter ?? (useHostFallback ? initialTargetFilter : undefined))
    : undefined;
  const effectiveSourcePath = isEditing
    ? (activeRel?.sourcePath ?? (useHostFallback ? initialSourcePath : undefined))
    : initialSourcePath;

  const handleTabClick = (id: string) => {
    if (id === activeRelId) return;
    setActiveRelId(id);
    setInAddMode(false);
    track('feature:loose_fk_editor_tab_switch');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] flex flex-col gap-0 p-0"
        data-testid="loose-fk-editor-dialog"
        onCloseAutoFocus={onCloseAutoFocus}
      >
        {/* Sticky header region — title + (i)/Add-another + multi-FK tab
            strip. `shrink-0` so it never collapses; the body below scrolls. */}
        <div className="shrink-0 px-6 pt-5 pb-3 border-b border-border space-y-2">
        <DialogHeader>
          <div className="flex items-center justify-between gap-2 pr-4">
            <div className="flex items-center gap-1.5 min-w-0">
              <DialogTitle className="text-sm truncate">{title}</DialogTitle>
              {/* Only while EDITING an existing declaration: in add-another
                  mode the form is mid-creation and belongs to nobody yet.
                  Saving from here is a manual update, so the flag this badge
                  shows is gone by the time the row re-renders. */}
              {isEditing && <ProvenanceBadge provenance={activeRel?.provenance} />}
              <DocsLinkButton
                url="https://jamsql.com/docs/loose-foreign-keys/"
                doc="loose-foreign-keys"
              />
            </div>
            {isEditing && (
              <div className="flex items-center gap-1.5 shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setInAddMode(true)}
                  data-testid="loose-fk-editor-add-another"
                  title="Declare another loose FK on this column"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Add another
                </Button>
                <Tooltip delayDuration={150}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center justify-center text-muted-foreground hover:text-foreground"
                      aria-label="Why multiple loose FKs on one column?"
                      data-testid="loose-fk-multi-info"
                      onClick={(e) => e.preventDefault()}
                    >
                      <Info className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    align="end"
                    className="max-w-sm whitespace-normal text-left leading-snug"
                  >
                    <div className="font-medium mb-1">
                      A single column can host multiple loose FKs
                    </div>
                    <div className="mb-1.5">
                      <span className="font-medium">Polymorphic dispatch.</span>{' '}
                      Give each declaration a source filter on a discriminator
                      column — e.g.{' '}
                      <span className="font-mono">commentable_type = &apos;Post&apos;</span>{' '}
                      vs{' '}
                      <span className="font-mono">commentable_type = &apos;Photo&apos;</span>
                      {' '}— so each one activates on a disjoint slice of rows.
                    </div>
                    <div>
                      <span className="font-medium">JSON columns.</span> Different
                      JSON paths inside the same column can point at different
                      parent tables — e.g.{' '}
                      <span className="font-mono">$.user_id</span> →{' '}
                      <span className="font-mono">users</span> alongside{' '}
                      <span className="font-mono">$.org_id</span> →{' '}
                      <span className="font-mono">orgs</span>.
                    </div>
                  </TooltipContent>
                </Tooltip>
              </div>
            )}
          </div>
        </DialogHeader>
        {/* Multi-FK tab strip — surfaces every declaration on the source
            triple so the user can switch between them without closing the
            dialog. Hidden in add-another mode (the form is mid-creation). */}
        {!inAddMode && allRels.length > 1 && (
          <div
            className="flex gap-1 flex-wrap"
            data-testid="loose-fk-editor-tabs"
          >
            {allRels.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => handleTabClick(r.id)}
                className={
                  r.id === activeRelId
                    ? 'px-2 py-0.5 text-xs rounded bg-primary text-primary-foreground'
                    : 'px-2 py-0.5 text-xs rounded border hover:bg-accent'
                }
                data-testid={`loose-fk-editor-tab-${r.id}`}
                title={`${r.toSchema}.${r.toTable}.${r.toColumn}`}
              >
                {renderLooseFkLabel(r)}
              </button>
            ))}
          </div>
        )}
        </div>
        {/* Scrollable body + sticky footer are owned by the picker's
            `scrollable` shell: flex-1 scroll region for the form, pinned
            action bar at the bottom. */}
        <div className="flex-1 min-h-0 flex flex-col">
        <LooseRelationshipTargetPicker
          scrollable
          key={isEditing ? `edit:${activeRelId}` : 'add'}
          connectionId={connectionId}
          database={database}
          source={source}
          allTables={allTables}
          engine={engine}
          existingRelationshipId={effectiveExistingId}
          initialTarget={effectiveTarget}
          initialSourceFilter={effectiveSourceFilter}
          initialTargetFilter={effectiveTargetFilter}
          initialSourcePath={effectiveSourcePath}
          addedSource={effectiveAddedSource}
          metadataEditors={metadataEditors}
          onSaved={(rel) => {
            // When the user flipped Add-another from inside an edit dialog,
            // keep the dialog open and snap the new declaration into the
            // active tab — so the multi-FK tab strip immediately shows it
            // and the user can keep editing.
            if (switchedFromEdit) {
              setInAddMode(false);
              setActiveRelId(rel.id);
              return;
            }
            if (onSaved) {
              onSaved(rel);
            } else {
              onOpenChange(false);
            }
          }}
          onCancel={() => {
            // Cancel from an in-dialog Add-another flip drops back to the
            // edit form for the previously-active tab rather than closing.
            if (switchedFromEdit) {
              setInAddMode(false);
              return;
            }
            if (onCancel) {
              onCancel();
            } else {
              onOpenChange(false);
            }
          }}
        />
        </div>
      </DialogContent>
    </Dialog>
  );
}
